import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useQuoteFormValidation} from 'Quoting/components/hooks/useQuoteFormValidation';
import {CheckboxInputGrid, MappingState} from 'Quoting/components/shared/CheckboxInputGrid';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {A360ProductCd} from 'Quoting/models/Quoting';
import {isDefined} from 'Quoting/utils/utilities';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {useBenefitToggle} from '../../PlanTemplates/CI/GCI5Utilities';
import {
  getBenefitArrayPaths,
  getGCI5HealthScreeningServicesPaths,
  getParentSchedulePath,
} from '../Common/PropertyPaths';
import {getFieldError, getValidationRuleset} from '../rules/rulesValidator';

const coverageIndex = 0;

const Portability = () => {
  const rows: GridRowProps[] = [];
  return <FormRowGrid rows={rows} />;
};

const WaiverOfPremium = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'waiverOfPremiumDetails';
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'waiver-of-permium-row-1'},
      items: [
        {
          key: 'waiver-of-permium-type',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CIWaiverOfPremiumBenefit.waiverTypeCd`}
              label="Type"
              value={getQuoteFieldValue<string>(additionalOptions.waiverTypeCd)}
              id={additionalOptions.waiverTypeCd}
              onChange={e => updateQuoteFieldValue(additionalOptions.waiverTypeCd, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'waiver-of-permium-waiting-period-days',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CIWaiverOfPremiumBenefit.waitingPeriod`}
              label="Waiting Period (Days)"
              value={getQuoteFieldValue<string>(additionalOptions.waitingPeriod)}
              id={additionalOptions.waitingPeriod}
              onChange={e => updateQuoteFieldValue(additionalOptions.waitingPeriod, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'waiver-of-permium-row-2'},
      items: [
        {
          key: 'waiver-of-permium-benefit-duration',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CIWaiverOfPremiumBenefit.benefitDuration`}
              label="Benefit Duration (Months)"
              value={getQuoteFieldValue<string>(additionalOptions.benefitDuration)}
              id={additionalOptions.benefitDuration}
              onChange={e => updateQuoteFieldValue(additionalOptions.benefitDuration, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const SkinCancer = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'skinCancerRider';
  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    benefitName,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);

  const skinCancerAmountError = getFieldError(
    'CISkinCancerRider.benefitAmount',
    getQuoteFieldValue<number>(additionalOptions.benefitAmount),
    getValidationRuleset(A360ProductCd.CI)
  );

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'skin-cancer-row-1'},
      items: [
        {
          key: 'skin-cancer-type-of-rider',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CISkinCancerRider.benefitTypeCd`}
              label="Benefit Type"
              value={getQuoteFieldValue<string>(additionalOptions.benefitTypeCd)}
              id={additionalOptions.benefitTypeCd}
              onChange={e => updateQuoteFieldValue(additionalOptions.benefitTypeCd, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'skin-cancer-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              value={getQuoteFieldValue<number>(additionalOptions.benefitAmount)}
              id={additionalOptions.benefitAmount}
              onChange={e => updateQuoteFieldValue(additionalOptions.benefitAmount, parseInt(e.target.value))}
              adornment="money"
              error={Boolean(skinCancerAmountError)}
              helperText={skinCancerAmountError}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'skin-cancer-row-2'},
      items: [
        {
          key: 'skin-cancer-days',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CISkinCancerRider.timePeriod`}
              label="Time Period"
              value={getQuoteFieldValue<string>(additionalOptions.timePeriod)}
              id={additionalOptions.timePeriod}
              onChange={e => updateQuoteFieldValue(additionalOptions.timePeriod, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'skin-cancer-row-3'},
      items: [
        {
          key: 'skin-cancer-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'skin-cancer-row-4'},
      items: [
        {
          key: 'skin-cancer-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const SpecifiedCondNInfectiousDisease = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const handleValidationOnBlur = useQuoteFormValidation();
  const benefitName = 'specConditionAndInfDiseaseDetails';
  const getExclusionPath = (benefitName: string) =>
    getBenefitLabelMapping(
      `${benefitName}Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    benefitName,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const SpecConditionError = getFieldError(
    'CISpecConditionAndInfDiseaseBenefit.planBnfAmountPct',
    getQuoteFieldValue<number>(additionalOptions.planBnfAmountPct) * 100,
    getValidationRuleset(A360ProductCd.CI)
  );
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'spec-cond-infectious-disease-row-1'},
      items: [
        {
          key: 'spec-cond-infectious-disease-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="% of Plan Benefit Amount (Recommended 25%)"
              type="number"
              value={getQuoteFieldValue<number>(additionalOptions.planBnfAmountPct)}
              id={additionalOptions.planBnfAmountPct}
              onChange={e => updateQuoteFieldValue(additionalOptions.planBnfAmountPct, Number(e.target.value) / 100)}
              adornment="percentage"
              error={isDefined(SpecConditionError)}
              onBlur={() => {
                handleValidationOnBlur();
              }}
              helperText={SpecConditionError}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'spec-cond-infectious-disease-row-2'},
      items: [
        {
          key: 'spec-cond-infectious-disease-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'spec-cond-infectious-disease-row-4'},
      items: [
        {
          key: 'spec-cond-infectious-disease-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const SpecifiedChronicIllness = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'specifiedChronicIllnessRider';
  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);
  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    benefitName,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'spec-chronic-illness-row-1'},
      items: [
        {
          key: 'spec-chronic-illness-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="% of Plan Benefit Amount (Recommended 50%)"
              type="number"
              value={getQuoteFieldValue<string>(additionalOptions.planBenefitAmountPct)}
              id={additionalOptions.planBenefitAmountPct}
              onChange={e => updateQuoteFieldValue(additionalOptions.planBenefitAmountPct, e.target.value)}
              adornment="percentage"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-row-2'},
      items: [
        {
          key: 'spec-chronic-illness-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-row-4'},
      items: [
        {
          key: 'spec-chronic-illness-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const SpecifiedChronicIllnessAndAccidentalInjury = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'specifiedChronicIllnessAndAccidentalInjuryRider';
  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);
  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    benefitName,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'spec-chronic-illness-accidental-injury-row-1'},
      items: [
        {
          key: 'spec-chronic-illness-accidental-injury-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="% of Plan Benefit Amount (Recommended 50%)"
              type="number"
              value={getQuoteFieldValue<string>(additionalOptions.planBenefitAmountPct)}
              id={additionalOptions.planBenefitAmountPct}
              onChange={e => updateQuoteFieldValue(additionalOptions.planBenefitAmountPct, e.target.value)}
              adornment="percentage"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-accidental-injury-row-2'},
      items: [
        {
          key: 'spec-chronic-illness-accidental-injury-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-accidental-injury-row-3'},
      items: [
        {
          key: 'spec-chronic-illness-accidental-injury-InjuryPct',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Injury"
              value={getQuoteFieldValue<number>(additionalOptions.injuryPct)}
              id={additionalOptions.injuryPct}
              adornment="percentage"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-accidental-injury-row-4'},
      items: [
        {
          key: 'spec-chronic-illness-accidental-injury-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const FixedBenefitCategoriesBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'healthScreeningDetails';
  const additionalOptions = getGCI5HealthScreeningServicesPaths(selectedPlan?.planIndex, coverageIndex);

  const fixedPaths = getBenefitLabelMapping(
    `${benefitName}FixedBenefit`,
    `${getParentSchedulePath(0, 0)}.${benefitName}`,
    {type: 'options', flag: 'isOptionApplied'}
  );
  return (
    <FormWrapper title="" withSeparator key="fixed-options">
      <Grid container spacing={2} style={{padding: '1rem'}}>
        <Grid item xs={12} md={6}>
          <QuoteFormElement
            elementType="Text"
            label="Benefit Amount (Per Unit)"
            type="number"
            value={getQuoteFieldValue<number>(additionalOptions.benefitAmount)?.toFixed(2)}
            id={additionalOptions.benefitAmount}
            onChange={e => updateQuoteFieldValue(additionalOptions.benefitAmount, Number(e.target.value))}
            adornment="money"
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <QuoteFormElement
            elementType="FormControl"
            controlType="switch"
            label="Preventive"
            checked={getQuoteFieldValue<boolean>(additionalOptions.isPreventativeEnabled)}
            id={additionalOptions.isPreventativeEnabled}
            onChange={e => updateQuoteFieldValue(additionalOptions.isPreventativeEnabled, e.target.value)}
          />
        </Grid>
        <FormWrapper title="Basic">
          <Grid item xs={12}>
            <CheckboxInputGrid state={fixedPaths} gridLength={3} />
          </Grid>
        </FormWrapper>
      </Grid>
    </FormWrapper>
  );
};

export const VariableBenefitCategoriesBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefit = 'healthScreeningDetails';
  const additionalOptions = getGCI5HealthScreeningServicesPaths(selectedPlan?.planIndex, coverageIndex);

  const paths = [0, 1, 2].map(catIndex =>
    getBenefitLabelMapping(
      `${benefit}VariableCategory${catIndex}`,
      `${getParentSchedulePath(selectedPlan?.planIndex)}.${benefit}`,
      {
        type: 'options',
        flag: 'isOptionApplied',
      }
    )
  );

  const CategoryPaths = getBenefitLabelMapping(
    `${benefit}VariableOptions`,
    `${getParentSchedulePath(selectedPlan?.planIndex)}.${benefit}`,
    {type: 'optionCategories', flag: 'value'}
  );

  return (
    <>
      {Array.isArray(paths) &&
        paths.map((mappingState, index) => (
          <FormWrapper title={'Category ' + (index + 1)} collapsible withSeparator key={index}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <QuoteFormElement
                  elementType="Text"
                  label="Benefit Amount"
                  type="number"
                  value={getQuoteFieldValue<string>(CategoryPaths[index].path)}
                  id={CategoryPaths[index].path}
                  onChange={e => updateQuoteFieldValue(CategoryPaths[index].path, e.target.value)}
                  adornment="money"
                />
              </Grid>
              {index == 0 && (
                <Grid item xs={12} md={6}>
                  <QuoteFormElement
                    elementType="FormControl"
                    controlType="switch"
                    label="Preventive"
                    checked={getQuoteFieldValue<boolean>(additionalOptions.isPreventativeEnabled)}
                    id={additionalOptions.isPreventativeEnabled}
                    onChange={e => updateQuoteFieldValue(additionalOptions.isPreventativeEnabled, e.target.value)}
                  />
                </Grid>
              )}
              <FormWrapper title="Basic">
                <Grid item xs={12}>
                  <CheckboxInputGrid state={mappingState} gridLength={4} />
                </Grid>
              </FormWrapper>
            </Grid>
          </FormWrapper>
        ))}
    </>
  );
};

const HealthScreeningServices = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'healthScreeningDetails';
  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}CIExclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getGCI5HealthScreeningServicesPaths(selectedPlan?.planIndex, coverageIndex);

  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const hashUnits = getQuoteFieldValue<number>(additionalOptions.healthScreeningHashUnits);
  const screenType = getQuoteFieldValue<string>(additionalOptions.healthScreeningType);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'health-screening-row-1'},
      items: [
        {
          key: 'health-screening-type-of-benefit',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CiHealthScreeningBenefit.healthScreeningType`}
              label="Type of Benefit"
              value={getQuoteFieldValue<string>(additionalOptions.healthScreeningType)}
              id={additionalOptions.healthScreeningType}
              onChange={e => updateQuoteFieldValue(additionalOptions.healthScreeningType, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'health-screening-hash-units',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="# of Units"
              value={getQuoteFieldValue<number>(additionalOptions.healthScreeningHashUnits)}
              id={additionalOptions.healthScreeningHashUnits}
              onChange={e => updateQuoteFieldValue(additionalOptions.healthScreeningHashUnits, Number(e.target.value))}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'health-screening-days',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CiHealthScreeningBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<string>(additionalOptions.timePeriodCd)}
              id={additionalOptions.timePeriodCd}
              onChange={e => updateQuoteFieldValue(additionalOptions.timePeriodCd, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {
        key: 'health-screening-row-2',
        hidden: hashUnits === undefined ? true : hashUnits < 1 || screenType !== 'Fixed' ? true : false,
      },
      items: [
        {
          key: 'health-screening-fixed-options',
          element: (
            <Grid container>
              <FixedBenefitCategoriesBlock />
            </Grid>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {
        key: 'health-screening-row-3',
        hidden: hashUnits === undefined ? true : hashUnits < 1 || screenType !== 'Variable' ? true : false,
      },
      items: [
        {
          key: 'health-screening-variable-options',
          element: (
            <Grid container>
              <VariableBenefitCategoriesBlock />
            </Grid>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'health-screening-row-4'},
      items: [
        {
          key: 'health-screening-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const MentalNervousDisorderAndSubstanceAbuse = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'mentalNervousDisorderAndSubstanceAbuseRider';
  const mentalNervousDisorderBenefitName = `${benefitName}.mentalNervousDisorder`;
  const substanceAbuseBenefitName = `${benefitName}.substanceAbuse`;
  const mentalNervousDisorder = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    mentalNervousDisorderBenefitName
  );

  const substanceAbuse = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, substanceAbuseBenefitName);

  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'mental-nervous-disorder-row-1'},
      title: 'MENTAL / NERVOUS DISORDER',
      items: [
        {
          key: 'mental-nervous-disorder-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount (Per Day)"
              value={getQuoteFieldValue<number>(mentalNervousDisorder.benefitAmount)}
              id={mentalNervousDisorder.benefitAmount}
              onChange={e => updateQuoteFieldValue(mentalNervousDisorder.benefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'mental-nervous-disorder-max-num',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Maximum Number (Days Per Time Period)"
              options={[]}
              value={getQuoteFieldValue<number>(mentalNervousDisorder.maxDaysPerPeriod)}
              id={mentalNervousDisorder.maxDaysPerPeriod}
              onChange={e => updateQuoteFieldValue(mentalNervousDisorder.maxDaysPerPeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'mental-nervous-disorder-row-2'},
      items: [
        {
          key: 'mental-nervous-disorder-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(mentalNervousDisorder.timePeriod)}
              id={mentalNervousDisorder.timePeriod}
              onChange={e => updateQuoteFieldValue(mentalNervousDisorder.timePeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'substance-abuse-row-3'},
      title: 'SUBSTANCE ABUSE',
      items: [
        {
          key: 'substance-abuse-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount (Per Day)"
              value={getQuoteFieldValue<number>(substanceAbuse.benefitAmount)}
              id={substanceAbuse.benefitAmount}
              onChange={e => updateQuoteFieldValue(substanceAbuse.benefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'substance-abuse-max-num',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Maximum Number (Days Per Time Period)"
              options={[]}
              value={getQuoteFieldValue<number>(substanceAbuse.maxDaysPerPeriod)}
              id={substanceAbuse.maxDaysPerPeriod}
              onChange={e => updateQuoteFieldValue(substanceAbuse.maxDaysPerPeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'substance-abuse-row-4'},
      items: [
        {
          key: 'substance-abuse-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(substanceAbuse.timePeriod)}
              id={substanceAbuse.timePeriod}
              onChange={e => updateQuoteFieldValue(substanceAbuse.timePeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'mental-nervous-disorder-row-5'},
      items: [
        {
          key: 'mental-nervous-disorder-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const SecondEvaluation = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'secondEvaluationTransportationAndLodgingRider';
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const secondEvaluation = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.secondEvaluation`
  );

  const coveredPersonNonLocalTransportation = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.coveredPersonNonLocalTransportation`
  );

  const companionNonLocalTransportation = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.companionNonLocalTransportation`
  );

  const coveredPersonLodging = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.coveredPersonLodging`
  );
  const companionLodging = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.companionLodging`
  );

  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'second-evaluation-row-1'},
      items: [
        {
          key: 'second-evaluation-units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="# of Units"
              options={[]}
              value={getQuoteFieldValue<number>(additionalOptions.numOfUnits)}
              id={additionalOptions.numOfUnits}
              onChange={e => updateQuoteFieldValue(additionalOptions.numOfUnits, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-2'},
      title: 'SECOND EVALUATION',
      items: [
        {
          key: 'second-evaluation-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              value={getQuoteFieldValue<number>(secondEvaluation.benefitAmount)}
              id={secondEvaluation.benefitAmount}
              onChange={e => updateQuoteFieldValue(secondEvaluation.benefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-max',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Maximum Number (Consultation/Evaluations Per Treatment)"
              options={[]}
              value={getQuoteFieldValue<string>(secondEvaluation.maxNumberConsultationOrEvaluations)}
              id={secondEvaluation.maxNumberConsultationOrEvaluations}
              onChange={e => updateQuoteFieldValue(secondEvaluation.maxNumberConsultationOrEvaluations, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-3'},
      title: 'COVERED PERSON NON LOCAL TRANSPORTATION',
      items: [
        {
          key: 'second-evaluation-non-travel',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Non Local Travel Distance More Than (Miles)"
              options={[]}
              value={getQuoteFieldValue<string>(coveredPersonNonLocalTransportation.nonLocalTravelDistance)}
              id={coveredPersonNonLocalTransportation.nonLocalTravelDistance}
              onChange={e =>
                updateQuoteFieldValue(coveredPersonNonLocalTransportation.nonLocalTravelDistance, e.target.value)
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-air-transport',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Air Transportation Benefit Amount (Per Trip)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonNonLocalTransportation.airTransportationBenefitAmount)}
              id={coveredPersonNonLocalTransportation.airTransportationBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(
                  coveredPersonNonLocalTransportation.airTransportationBenefitAmount,
                  e.target.value
                )
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-ground-transport',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ground Transportation Benefit Amount (Per Mile)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonNonLocalTransportation.groundTransportationBenefitAmount)}
              id={coveredPersonNonLocalTransportation.groundTransportationBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(
                  coveredPersonNonLocalTransportation.groundTransportationBenefitAmount,
                  e.target.value
                )
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-ground-transport-max',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ground Transportation Maximum Miles (Per Round Trip)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonNonLocalTransportation.groundTransportationMaxMiles)}
              id={coveredPersonNonLocalTransportation.groundTransportationMaxMiles}
              onChange={e =>
                updateQuoteFieldValue(coveredPersonNonLocalTransportation.groundTransportationMaxMiles, e.target.value)
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-max-benefit',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount (Per Covered Person, Per Time Period)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonNonLocalTransportation.maxBenefitAmount)}
              id={coveredPersonNonLocalTransportation.maxBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(coveredPersonNonLocalTransportation.maxBenefitAmount, e.target.value)
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(coveredPersonNonLocalTransportation.timePeriod)}
              id={coveredPersonNonLocalTransportation.timePeriod}
              onChange={e => updateQuoteFieldValue(coveredPersonNonLocalTransportation.timePeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-4'},
      title: 'COMPANION NON LOCAL TRANSPORTATION',
      items: [
        {
          key: 'second-evaluation-companion-non-travel',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Non Local Travel Distance More Than (Miles)"
              options={[]}
              value={getQuoteFieldValue<string>(companionNonLocalTransportation.nonLocalTravelDistance)}
              id={companionNonLocalTransportation.nonLocalTravelDistance}
              onChange={e =>
                updateQuoteFieldValue(companionNonLocalTransportation.nonLocalTravelDistance, e.target.value)
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-air-transport',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Air Transportation Benefit Amount (Per Trip)"
              type="number"
              value={getQuoteFieldValue<number>(companionNonLocalTransportation.airTransportationBenefitAmount)}
              id={companionNonLocalTransportation.airTransportationBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(companionNonLocalTransportation.airTransportationBenefitAmount, e.target.value)
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-ground-transport',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ground Transportation Benefit Amount (Per Mile)"
              type="number"
              value={getQuoteFieldValue<number>(companionNonLocalTransportation.groundTransportationBenefitAmount)}
              id={companionNonLocalTransportation.groundTransportationBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(companionNonLocalTransportation.groundTransportationBenefitAmount, e.target.value)
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-ground-transport-max',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ground Transportation Maximum Miles (Per Round Trip)"
              type="number"
              value={getQuoteFieldValue<number>(companionNonLocalTransportation.groundTransportationMaxMiles)}
              id={companionNonLocalTransportation.groundTransportationMaxMiles}
              onChange={e =>
                updateQuoteFieldValue(companionNonLocalTransportation.groundTransportationMaxMiles, e.target.value)
              }
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-max-benefit',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount (Per Time Period)"
              type="number"
              value={getQuoteFieldValue<number>(companionNonLocalTransportation.maxBenefitAmount)}
              id={companionNonLocalTransportation.maxBenefitAmount}
              onChange={e => updateQuoteFieldValue(companionNonLocalTransportation.maxBenefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(companionNonLocalTransportation.timePeriod)}
              id={companionNonLocalTransportation.timePeriod}
              onChange={e => updateQuoteFieldValue(companionNonLocalTransportation.timePeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-5'},
      title: 'COVERED PERSON LODGING',
      items: [
        {
          key: 'second-evaluation-lodging-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonLodging.benefitAmount)}
              id={coveredPersonLodging.benefitAmount}
              onChange={e => updateQuoteFieldValue(coveredPersonLodging.benefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-lodging-max-benefit',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount (Per Covered Person, Per Time Period)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonLodging.maxBenefitAmount)}
              id={coveredPersonLodging.maxBenefitAmount}
              onChange={e => updateQuoteFieldValue(coveredPersonLodging.maxBenefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-lodging-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(coveredPersonLodging.timePeriod)}
              id={coveredPersonLodging.timePeriod}
              onChange={e => updateQuoteFieldValue(coveredPersonLodging.timePeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-6'},
      title: 'COMPANION LODGING',
      items: [
        {
          key: 'second-evaluation-companion-lodging-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              type="number"
              value={getQuoteFieldValue<number>(companionLodging.benefitAmount)}
              id={companionLodging.benefitAmount}
              onChange={e => updateQuoteFieldValue(companionLodging.benefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-lodging-max-benefit',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount (Per Time Period)"
              type="number"
              value={getQuoteFieldValue<number>(companionLodging.maxBenefitAmount)}
              id={companionLodging.maxBenefitAmount}
              onChange={e => updateQuoteFieldValue(companionLodging.maxBenefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-lodging-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(companionLodging.timePeriod)}
              id={companionLodging.timePeriod}
              onChange={e => updateQuoteFieldValue(companionLodging.timePeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-7'},
      items: [
        {
          key: 'second-evaluation-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const LifeStyleEnhancement = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'lifestyleEnhancementRider';
  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);
  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    benefitName,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );
  const otherBenefitPath = `${getParentSchedulePath(
    selectedPlan?.planIndex,
    coverageIndex
  )}.${benefitName}.isOtherEligiblePrograms`;
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'lifestyle-enhancement-row-1'},
      items: [
        {
          key: 'lifestyle-enhancement-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              value={getQuoteFieldValue<number>(additionalOptions.benefitAmount)}
              id={additionalOptions.benefitAmount}
              onChange={e => updateQuoteFieldValue(additionalOptions.benefitAmount, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'lifestyle-enhancement-days',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(additionalOptions.timePeriod)}
              id={additionalOptions.timePeriod}
              onChange={e => updateQuoteFieldValue(additionalOptions.timePeriod, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'lifestyle-enhancement-row-2'},
      items: [
        {
          key: 'lifestyle-enhancement-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'lifestyle-enhancement-row-3'},
      items: [
        {
          key: 'lifestyle-enhancement-other-option',
          element: getQuoteFieldValue<boolean>(otherBenefitPath) && (
            <QuoteFormElement
              elementType="Text"
              label="Additional Eligible Programs"
              value={getQuoteFieldValue<string>(additionalOptions.additionalEligiblePrograms)}
              id={additionalOptions.additionalEligiblePrograms}
              onChange={e => updateQuoteFieldValue(additionalOptions.additionalEligiblePrograms, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'lifestyle-enhancement-row-4'},
      items: [
        {
          key: 'lifestyle-enhancement-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AdditionalOptionsandBenefitsGCI5Block = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  

  const benefitSchedulePath = getParentSchedulePath(selectedPlan?.planIndex, coverageIndex);
  const {onToggleBenefitApply} = useBenefitToggle();
  const additionalOptNBenefits: ConditionalSection[] = [
    {
      Component: Portability,
      key: 'Portability',
      label: 'PORTABILITY (UNLIMITED)',
      disabled: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isPortabilityApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isPortabilityApplied`, visibility),
    },
    {
      Component: WaiverOfPremium,
      key: 'WaiverOfPremium',
      label: 'WAIVER OF PREMIUM',
      disabled: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isWaiverOfPremiumApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isWaiverOfPremiumApplied`, visibility),
    },
    {
      Component: SkinCancer,
      key: 'SkinCancer',
      label: 'SKIN CANCER',
      disabled: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSkinCancerRiderApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isSkinCancerRiderApplied`, visibility),
    },
    {
      Component: SpecifiedCondNInfectiousDisease,
      key: 'SpecifiedCondNInfectiousDisease',
      label: 'SPECIFIED CONDITION AND INFECTIOUS DISEASE',
      disabled: true,
      hidden: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSpecConditionAndInfDiseaseApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isSpecConditionAndInfDiseaseApplied`, visibility),
    },
    {
      Component: SpecifiedChronicIllness,
      key: 'SpecifiedChronicIllness',
      label: 'SPECIFIED CHRONIC ILLNESS (90 DAY ELIMINATION PERIOD)',
      disabled: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSpecifiedChronicIllnessRiderApplied`),
      onToggleVisibility: (visibility: boolean) =>
        onToggleBenefitApply(
          'CISpecifiedChronicIllnessRider',
          `${benefitSchedulePath}.isSpecifiedChronicIllnessRiderApplied`,
          visibility,
          `${benefitSchedulePath}.specifiedChronicIllnessRider`
        ),
    },
    {
      Component: SpecifiedChronicIllnessAndAccidentalInjury,
      key: 'SpecifiedChronicIllnessAndAccidentalInjury',
      label: 'SPECIFIED CHRONIC ILLNESS AND ACCIDENTAL INJURY (365 DAY ELIMINATION PERIOD)',
      disabled: true, 
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSpecifiedChronicIllnessAndAccidentalInjuryRiderApplied`),
      onToggleVisibility: (visibility: boolean) =>
        onToggleBenefitApply(
          'CISpecifiedChronicIllnessAndAccidentalInjuryRider',
          `${benefitSchedulePath}.isSpecifiedChronicIllnessAndAccidentalInjuryRiderApplied`,
          visibility,
          `${benefitSchedulePath}.specifiedChronicIllnessAndAccidentalInjuryRider`
        ),
    },
    {
      Component: HealthScreeningServices,
      key: 'HealthScreeningServices',
      label: 'HEALTH SCREENING SERVICES',
      disabled: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isHealthScreeningApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isHealthScreeningApplied`, visibility),
    },
    {
      Component: MentalNervousDisorderAndSubstanceAbuse,
      key: 'MentalNervousDisorderAndSubstanceAbuse',
      hidden: true,
      label: 'MENTAL OR NERVOUS DISORDER AND SUBSTANCE ABUSE',
    },
    {
      Component: LifeStyleEnhancement,
      key: 'LifeStyleEnhancement',
      hidden: true,
      label: 'LIFESTYLE ENHANCEMENT',
    },
    {
      Component: SecondEvaluation,
      key: 'secondEvaluation',
      hidden: true,
      label: 'SECOND EVALUATION, TRANSPORTATION, AND LODGING',
    },
  ];

  return (
    <FormWrapper title="Additional Options and Benefits" collapsible withSeparator expanded>
      <QuoteFormConditionalSection sections={additionalOptNBenefits} />
    </FormWrapper>
  );
};
